import { BuildingType, dollars } from './buildings';

/*
 * Milestones: the airport's goals, from the first steps (a tower, a runway, a yellow line) to
 * growing up (narrowbodies, widebodies, a good rating). Each pays a reward once, the moment it
 * is reached. The first ones double as the tutorial: the UI shows the next few as objectives.
 * Ids are saved as bits: append only, never reorder.
 */

export interface MilestoneFacts {
  /** Best completed level per building type. */
  best: (type: number) => number;
  /** A terminal linked to the motorway by road; a stand with a road; runway and stand linked. */
  roadAccess: boolean;
  standRoad: boolean;
  connected: boolean;
  contracts: number;
  departures: number;
  rating: number;
  money: number;
  /** Departures by aircraft class (1 regional, 2 narrowbody, 3 widebody). */
  departuresOf: (cls: number) => number;
  /** Freight flights handled (today and yesterday: enough to notice the first). */
  freight: number;
  /** Connecting passengers flown out in a day (the better of today and yesterday). */
  transfers: number;
  /** Contracts with international airlines. */
  international: number;
  /** Charter contracts under way. */
  charters: number;
  /** What the airport is worth (see finance.ts). */
  value: number;
}

export interface MilestoneDef {
  readonly id: number;
  readonly key: string;
  readonly reward: number;
  readonly done: (f: MilestoneFacts) => boolean;
  /** Part of the first-game tutorial. */
  readonly tutorial: boolean;
}

const m = (
  id: number,
  key: string,
  reward: number,
  done: (f: MilestoneFacts) => boolean,
  tutorial = false,
): MilestoneDef => ({ id, key, reward: dollars(reward), done, tutorial });

/**
 * The goals, in the order they come. Their rewards help a young airport along; the money is
 * meant to come from running it (halved in SIM 0.47.0: the goals had paid four times what the
 * flights earned in the first days).
 */
export const MILESTONES: readonly MilestoneDef[] = [
  m(0, 'tower', 5_000, (f) => f.best(BuildingType.Tower) >= 1, true),
  m(
    1,
    'runway',
    5_000,
    (f) => f.best(BuildingType.Runway) >= 1 || f.best(BuildingType.MainRunway) >= 1,
    true,
  ),
  m(2, 'terminal', 5_000, (f) => f.best(BuildingType.Terminal) >= 1, true),
  m(3, 'stand', 2_500, (f) => f.best(BuildingType.Stand) >= 1, true),
  m(4, 'fire', 2_500, (f) => f.best(BuildingType.FireStation) >= 1, true),
  m(5, 'roadAccess', 2_500, (f) => f.roadAccess, true),
  m(6, 'standRoad', 2_500, (f) => f.standRoad, true),
  m(7, 'taxiLine', 5_000, (f) => f.connected, true),
  m(8, 'contract', 2_500, (f) => f.contracts > 0, true),
  m(9, 'firstFlight', 7_500, (f) => f.departures >= 1, true),
  m(10, 'flights10', 12_500, (f) => f.departures >= 10),
  m(11, 'handling', 5_000, (f) => f.best(BuildingType.GroundHandling) >= 1),
  m(12, 'rating', 10_000, (f) => f.rating >= 700),
  // A lit runway (the runway's third level).
  m(
    13,
    'runway2',
    15_000,
    (f) => f.best(BuildingType.Runway) >= 2 || f.best(BuildingType.MainRunway) >= 3,
  ),
  m(14, 'narrowbody', 20_000, (f) => f.departuresOf(2) >= 1),
  m(15, 'flights50', 25_000, (f) => f.departures >= 50),
  m(16, 'transit', 7_500, (f) => f.best(BuildingType.TransitHub) >= 1),
  m(17, 'terminal3', 30_000, (f) => f.best(BuildingType.Terminal) >= 3),
  m(18, 'widebody', 50_000, (f) => f.departuresOf(3) >= 1),
  m(19, 'flights200', 50_000, (f) => f.departures >= 200),
  m(20, 'millionaire', 0, (f) => f.money >= dollars(1_000_000)),
  m(21, 'rating5', 75_000, (f) => f.rating >= 900),
  m(22, 'freight', 20_000, (f) => f.freight >= 1),
  m(23, 'hub', 30_000, (f) => f.transfers >= 100),
  m(24, 'international', 25_000, (f) => f.international >= 1),
  m(25, 'office', 5_000, (f) => f.best(BuildingType.ConstructionOffice) >= 1),
  m(26, 'hotel', 15_000, (f) => f.best(BuildingType.Hotel) >= 1),
  m(27, 'kitchen', 10_000, (f) => f.best(BuildingType.CateringKitchen) >= 1),
  m(28, 'charter', 12_500, (f) => f.charters >= 1),
  m(29, 'rail', 30_000, (f) => f.best(BuildingType.RailStation) >= 1),
  m(30, 'value10m', 0, (f) => f.value >= dollars(10_000_000)),
];

/** Completed milestones are bits of an unsigned 32-bit number: ids stay below 32. */
export function milestoneDone(bits: number, id: number): boolean {
  return ((bits >>> id) & 1) === 1;
}

export function withMilestone(bits: number, id: number): number {
  return (bits | (1 << id)) >>> 0;
}
